/**
 * How the active cart is decided to be stale.
 *
 * The rule, deliberately: staleness is a function of the order's AGE, checked
 * ONCE when the customer lands, before they do any work. It is NOT decided
 * mid-flow from which client-side store (cookie / localStorage / URL) happens
 * to be readable at a given instant.
 *
 * That distinction is the whole fix. The session was persisted in three stores
 * with different lifetimes (cookie 47h, localStorage 8h, URL until navigation),
 * and the checkout path treated "I cannot read any of them right now" as "this
 * cart is dead" -- so it wiped a live cart and started an empty order whenever a
 * cookie was transiently evicted (routine on mobile Safari / in-app browsers).
 * Deciding staleness by real age, on load, removes that class of false positive:
 * an order the customer just started is never stale, no matter which store
 * blipped.
 */

import { OrderStatus } from "src/enums/order";

// Order statuses that mean the order is DONE and must no longer act as the cart:
// keeping any of these as the active order would show a paid/placed order in the
// drawer/badge and let new items be added to a completed order.
//
// Lives here (not in contexts/index.tsx, where it used to) so the restore
// decision can be a pure, directly testable function. There is exactly ONE such
// list — contexts imports this one.
export const FINALIZED_ORDER_STATUSES = new Set<string>([
  OrderStatus.PAID,
  OrderStatus.PAYMENT_ON_DELIVERY,
  OrderStatus.IN_PRODUCTION,
  OrderStatus.SHIPPED,
  OrderStatus.DELIVERED,
  OrderStatus.REFUNDED,
  OrderStatus.PARTIALLY_REFUNDED,
  OrderStatus.CANCELED,
]);

// The ceiling is bounded from above by how long the photos themselves live.
// Retention is a storage folder rule, not something the client sets: uploads
// are deleted 7 days after upload on MinIO, and 21 days once storage is on R2.
// The backend's own UNPAID_PHOTO_RETENTION_DAYS = 14 therefore never takes
// effect on MinIO -- storage wins. The ceiling below stays sized for the
// shorter MinIO window until a deliberate follow-up raises it.
//
// So a cart must never outlive its photos: resuming a cart whose objects have
// been swept shows the customer line items with dead image URLs, which is worse
// than asking them to re-add. 4 days keeps 3 days of margin under that hard 7 --
// and the stores (below) sit a day above the ceiling, so the real distance from
// the sweep is 2 days, not 3.
//
// This was 2 days, which was needlessly aggressive on the same 7-day budget: a
// customer who uploaded on Friday and came back Monday lost a cart whose photos
// were still sitting in the bucket. Observed on staging with a 2.4-day-old
// order holding 14 photos and EUR 57.91. 4 covers that weekend case -- Friday
// evening to Monday evening is ~3 days -- with a day to spare, which is the
// span this number actually has to buy.
export const CART_MAX_AGE_DAYS = 4;

/**
 * How much longer than the age ceiling the client-side stores are kept alive.
 *
 * The stores must OUTLIVE the cart, never the reverse. When CART_MAX_AGE_DAYS
 * went from 2 to 5 the policy change was inert: the `orderId` and `guest_token`
 * cookies were pinned at 47h and the localStorage backup at 48h, so on day three
 * there was nothing left to restore from and the app quietly started a fresh
 * order. The ceiling was raised and nothing happened — the failure was silent,
 * because no store referenced the ceiling at all.
 *
 * One day of margin (25% over a 4-day ceiling), chosen because it is the
 * smallest slack that covers the three ways a store's clock and the order's real
 * age disagree:
 *   - the cookie's expiry is measured from the last time setOrderInfo ran, which
 *     is usually order creation but can be earlier than the server's createdAt
 *     by a round trip;
 *   - cookie/localStorage expiry is evaluated against the DEVICE clock while the
 *     age check uses the server's createdAt, and phone clocks routinely drift by
 *     minutes to hours;
 *   - a customer resuming on the last legal day must find the pair still there
 *     to be resumed at all.
 *
 * The margin cannot resurrect a cart past the ceiling: staleness is still
 * decided on landing from the server's createdAt (see isOrderStaleByAge), so all
 * the extra day buys is that the AGE CHECK gets to make the decision instead of
 * a store silently expiring first. It also stays comfortably inside the 7-day
 * photo lifetime above: 4 + 1 = 5.
 */
const CART_STORAGE_MARGIN_DAYS = 1;

/**
 * How long every client-side store of the (orderId, guest token) pair lives.
 *
 * SINGLE SOURCE OF TRUTH. The cookies (`orderId` in contexts/index.tsx and
 * hooks/useOrderSession.ts, `guest_token` in services/api.ts) and the
 * localStorage backup (saveOrderData in utils/local-storage.util.ts) all derive
 * their lifetime from here, so raising CART_MAX_AGE_DAYS again can never leave
 * a store behind the way 47h/48h were left behind 5 days.
 */
export const ORDER_SESSION_STORAGE_MAX_AGE_DAYS =
  CART_MAX_AGE_DAYS + CART_STORAGE_MARGIN_DAYS;

/** Same lifetime in hours — the unit setWithExpiry/saveOrderData take. */
export const ORDER_SESSION_STORAGE_MAX_AGE_HOURS =
  ORDER_SESSION_STORAGE_MAX_AGE_DAYS * 24;

/** Same lifetime in seconds — the unit nookies' `maxAge` takes. */
export const ORDER_SESSION_COOKIE_MAX_AGE_SECONDS =
  ORDER_SESSION_STORAGE_MAX_AGE_HOURS * 60 * 60;

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Is an order too old to keep resuming as the active cart?
 *
 * @param createdAtIso the order's createdAt (ISO string from the API)
 * @param nowMs        current time in ms (injected so this stays pure/testable)
 * @param maxAgeDays   age ceiling; defaults to CART_MAX_AGE_DAYS
 * @returns true only when the age is known AND exceeds the ceiling. An
 *          unparseable or missing timestamp returns false -- never throw away a
 *          cart on a value we could not read.
 */
export function isOrderStaleByAge(
  createdAtIso: string | null | undefined,
  nowMs: number,
  maxAgeDays: number = CART_MAX_AGE_DAYS,
): boolean {
  if (!createdAtIso) return false;
  const created = Date.parse(createdAtIso);
  if (Number.isNaN(created)) return false;
  return nowMs - created > maxAgeDays * DAY_MS;
}

/**
 * Should a restored session (orderId + guest token recovered from the URL /
 * cookie on load) be dropped instead of resumed as the active cart?
 *
 * Call this ONLY with the body of a SUCCESSFUL order fetch. A failed fetch is
 * not a decision: a network error, timeout or 5xx must keep the cart, and the
 * caller handles the separate, definitive "gone" responses (404/401/403).
 *
 * Two, and only two, reasons to drop -- both of which require a value we could
 * actually read:
 *   1. the order is FINALIZED (paid / COD placed / shipped / ...). It is a
 *      completed purchase, not a cart; resuming it is how a customer's second
 *      order ended up appended to their first, already-paid one.
 *   2. the order is older than the age ceiling (see isOrderStaleByAge).
 *
 * Everything else keeps the cart, deliberately: a missing / empty / unknown
 * status, a missing or unparseable createdAt, an in-progress order of any age
 * under the ceiling. Wiping a live cart is worse than resuming a stale one.
 *
 * @param nowMs current time in ms (injected so this stays pure/testable)
 */
export function shouldDropRestoredCart(args: {
  status?: string | null;
  createdAt?: string | null;
  nowMs: number;
  maxAgeDays?: number;
}): boolean {
  return cartDropReason(args) !== null;
}

/**
 * Why a cart is dropped. The vocabulary the Sentry report uses, so an event can
 * be read without re-deriving the branch it came from.
 *
 * These are exactly the three doors in the code — there is deliberately no
 * fourth. Everything else keeps the cart.
 */
export type CartDropReason =
  | "finalized" // a completed purchase; correct to start fresh
  | "age" // past CART_MAX_AGE_DAYS, decided from the server's createdAt
  | "definitive-404-401-403"; // the server refused: gone, or not ours

/**
 * WHICH of the two success-branch reasons applies, or null to keep the cart.
 *
 * The same decision shouldDropRestoredCart makes — it now delegates here, so
 * there is one rule and not two — but it names the branch instead of collapsing
 * it to a boolean. That name is the whole point: "a cart was dropped" is not
 * actionable, and "a PENDING cart was dropped for age at 5.2 days, restored from
 * localStorage" is. Finalized is checked first, matching the order the callers
 * used when they computed `finalized` separately.
 *
 * The third reason, definitive-404-401-403, cannot be decided from a fetched
 * body — there is no body. It comes from shouldDropRestoredCartOnFetchError.
 */
export function cartDropReason(args: {
  status?: string | null;
  createdAt?: string | null;
  nowMs: number;
  maxAgeDays?: number;
}): "finalized" | "age" | null {
  const { status, createdAt, nowMs, maxAgeDays } = args;
  // Only a KNOWN finalized status counts. An unrecognised or absent value is
  // never evidence that the order is done.
  if (typeof status === "string" && FINALIZED_ORDER_STATUSES.has(status)) {
    return "finalized";
  }
  if (isOrderStaleByAge(createdAt, nowMs, maxAgeDays)) return "age";
  return null;
}

/**
 * How old the order is, in days, or null when that cannot be read.
 *
 * Diagnostics only. Reported alongside a drop so an "age" drop can be checked
 * against the ceiling rather than trusted — a cart dropped at 5.01 days is the
 * policy working, one dropped at 2.1 days means a store expired underneath it,
 * which is exactly the silent failure bca7d728 fixed and could not see.
 *
 * Rounded to one decimal: enough to tell 2.1 from 5.2, coarse enough that it
 * cannot become a per-customer identifier if it is ever promoted to a tag.
 */
export function orderAgeDays(
  createdAtIso: string | null | undefined,
  nowMs: number,
): number | null {
  if (!createdAtIso) return null;
  const created = Date.parse(createdAtIso);
  if (Number.isNaN(created)) return null;
  return Math.round(((nowMs - created) / DAY_MS) * 10) / 10;
}

/**
 * The other half of the same decision: the restore fetch FAILED. Should the
 * cart be dropped?
 *
 * shouldDropRestoredCart above answers only for a successful fetch and says so;
 * this is the companion it defers to, so the whole drop-vs-keep rule lives in
 * one file instead of being restated at each call site. Restating it is how the
 * home page ended up wiping carts on a plain network blip while the provider
 * next door kept them.
 *
 * ONLY a definitive answer from the server counts:
 *   404 — the order does not exist
 *   401 / 403 — this token is not (or no longer) allowed to see it
 *
 * Everything else KEEPS the cart: a network error, a DNS failure, a timeout, a
 * CORS rejection, an aborted request, a 500/502/503/504, or any error object
 * that carries no HTTP status at all. None of those is evidence about the
 * order — they are evidence about the connection, and the customer's photos
 * must not pay for a bad connection. The order is left alone and re-validated
 * on the next real action.
 *
 * @param err the rejection value from the order fetch (unknown by design: it
 *            may be an AxiosError, a TypeError from fetch, or anything else)
 */
export function shouldDropRestoredCartOnFetchError(err: unknown): boolean {
  const status = (err as { response?: { status?: number } } | null | undefined)
    ?.response?.status;
  return status === 404 || status === 401 || status === 403;
}
